export const OrderCutoffTime = 11;
export const BusinessDayCustoffTime = 17; // 3pm Brisbane time
export const brisbaneOffset = 10;
export const brisbaneTimezone = 'Australia/Brisbane';

type Dateish = Date | string | number;
function toDate(d: Dateish): Date {
	return d instanceof Date ? d : new Date(d);
}
// Extract Brisbane wall-clock parts, deterministically.
function getBrisbaneParts(d: Dateish) {
	const date = toDate(d);
	const parts = new Intl.DateTimeFormat('en-CA', {
		timeZone: brisbaneTimezone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
	}).formatToParts(date);

	const map = Object.create(null) as Record<string, string>;
	for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value;

	const weekdayStr = new Intl.DateTimeFormat('en-US', {
		timeZone: brisbaneTimezone,
		weekday: 'short',
	}).format(date); // "Mon".."Sun"

	const weekdayNum = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[weekdayStr];

	return {
		year: +map.year,
		month: +map.month, // 1-12
		day: +map.day, // 1-31
		hour: +map.hour, // 0-23
		minute: +map.minute,
		second: +map.second,
		weekdayNum,
	};
}

// Construct a UTC Date from Brisbane wall-clock Y/M/D h:m:s
// (i.e., "this Brisbane moment" expressed in UTC).
function brisbanePartsToUTCDate(p: { year: number; month: number; day: number; hour?: number; minute?: number; second?: number }): Date {
	const h = (p.hour ?? 0) - brisbaneOffset;
	return new Date(Date.UTC(p.year, p.month - 1, p.day, h, p.minute ?? 0, p.second ?? 0, 0));
}

// ===== Conversions (epoch shifting; safe because QLD has no DST) =====
/** Converts a UTC instant to a new Date whose epoch is shifted by +10h (i.e., "show me this in Brisbane time"). */
export function convertToBrisbaneTime(date: Dateish): Date {
	const t = toDate(date).getTime();
	return new Date(t + brisbaneOffset * 60 * 60 * 1000);
}

/** Converts a Brisbane wall-clock instant back to UTC by -10h. */
export function convertToUTC(brisbaneDate: Dateish): Date {
	const t = toDate(brisbaneDate).getTime();
	return new Date(t - brisbaneOffset * 60 * 60 * 1000);
}

export function getStartOfDay(d?: Dateish): Date {
	const p = getBrisbaneParts(d ?? new Date());
	return brisbanePartsToUTCDate({ year: p.year, month: p.month, day: p.day, hour: 0, minute: 0, second: 0 });
}

// ===== Business day helpers (Brisbane wall-clock) =====
export function isBusinessDay(d?: Dateish): boolean {
	const { weekdayNum } = getBrisbaneParts(d ?? new Date());
	return weekdayNum !== 0 && weekdayNum !== 6; // Mon..Fri
}

export function isBusinessDayPreCutoff(d?: Dateish): boolean {
	const p = getBrisbaneParts(d ?? new Date());
	return isBusinessDay(d) && p.hour < OrderCutoffTime;
}

export function isFridayPostCutoffOrSaturday(d?: Dateish): boolean {
	const p = getBrisbaneParts(d ?? new Date());
	return (p.weekdayNum === 5 && p.hour >= OrderCutoffTime) || p.weekdayNum === 6;
}

export function isBusinessDayPostEndOfHours(d?: Dateish): boolean {
	const p = getBrisbaneParts(d ?? new Date());
	return isBusinessDay(d) && p.hour >= BusinessDayCustoffTime;
}

/**
 * Moves forward to the next business day if the given date is a weekend
 */
export function getNextBusinessDay(d?: Dateish): Date {
	// Work in Brisbane wall-clock days, then return a UTC Date for 00:00 Brisbane.
	let p = getBrisbaneParts(d ?? new Date());
	// Start from this date's day; if weekend, bump until Mon..Fri.
	const tmp = new Date(Date.UTC(p.year, p.month - 1, p.day));
	while (!isBusinessDay(tmp)) {
		tmp.setUTCDate(tmp.getUTCDate() + 1);
	}
	const np = getBrisbaneParts(tmp);
	return brisbanePartsToUTCDate({ year: np.year, month: np.month, day: np.day });
}

/** Calculates due date (returns a UTC Date) based on Brisbane business rules. */
export function calculateDueDate(orderCreatedAt: Dateish, turnaround: number): Date {
	// Determine the start date in Brisbane wall-clock
	let p = getBrisbaneParts(orderCreatedAt);

	// If not business day pre-cutoff, start from next business day
	if (!isBusinessDayPreCutoff(orderCreatedAt)) {
		const d = new Date(Date.UTC(p.year, p.month - 1, p.day));
		d.setUTCDate(d.getUTCDate() + 1);
		// advance to next business day
		while (!isBusinessDay(d)) d.setUTCDate(d.getUTCDate() + 1);
		p = getBrisbaneParts(d);
	}

	// Add (turnaround - 1) business days
	let daysToAdd = Math.max(0, turnaround - 1);
	const cursor = new Date(Date.UTC(p.year, p.month - 1, p.day));
	while (daysToAdd > 0) {
		cursor.setUTCDate(cursor.getUTCDate() + 1);
		if (isBusinessDay(cursor)) daysToAdd--;
	}

	// Keep the original wall-clock time-of-day from the creation moment.
	const endParts = getBrisbaneParts(cursor);

	return brisbanePartsToUTCDate({
		year: endParts.year,
		month: endParts.month,
		day: endParts.day,
		//At the start of the day
		hour: 0,
		minute: 0,
		second: 0,
	});
}

// ===== Month start (Brisbane 00:00 on the 1st) as a UTC Date =====
export function getMonthStart(): Date {
	const p = getBrisbaneParts(new Date());
	return brisbanePartsToUTCDate({ year: p.year, month: p.month, day: 1, hour: 0, minute: 0, second: 0 });
}

// ===== Readable strings (hydration-safe if computed in loader or frozen) =====
export function getReadableDate(d: Dateish) {
	return new Intl.DateTimeFormat('en-AU', { timeZone: brisbaneTimezone }).format(toDate(d));
}

export function getReadableTime(d: Dateish) {
	// e.g., "3:07 pm"
	return new Intl.DateTimeFormat('en-AU', {
		timeZone: brisbaneTimezone,
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})
		.format(toDate(d))
		.replace(/\s/g, ''); // "3:07pm"
}

export function getReadableTimeDate(d: Dateish) {
	return `${getReadableTime(d)} ${getReadableDate(d)}`;
}
export function getReadableDateTime(d: Dateish) {
	return `${getReadableDate(d)} ${getReadableTime(d)}`;
}

// ===== ISO strings pinned to Brisbane wall-clock =====
export function toBrisbaneISO8601(date?: Dateish): string {
	const p = getBrisbaneParts(date ?? new Date());
	const mm = String(p.month).padStart(2, '0');
	const dd = String(p.day).padStart(2, '0');
	const hh = String(p.hour).padStart(2, '0');
	const mi = String(p.minute).padStart(2, '0');
	const ss = String(p.second).padStart(2, '0');
	return `${p.year}-${mm}-${dd}T${hh}:${mi}:${ss}+10:00`;
}

// ===== Day math (Brisbane civil day) =====
export function getDaysUntil(target: Dateish): number {
	const nowP = getBrisbaneParts(new Date());
	const tgtP = getBrisbaneParts(target);

	// Normalize both to Brisbane midnight
	const todayUTC = brisbanePartsToUTCDate({ year: nowP.year, month: nowP.month, day: nowP.day, hour: 0, minute: 0, second: 0 });
	const targetUTC = brisbanePartsToUTCDate({ year: tgtP.year, month: tgtP.month, day: tgtP.day, hour: 0, minute: 0, second: 0 });

	const diffMs = targetUTC.getTime() - todayUTC.getTime();
	const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (days < 0) return -1;
	return days; // 0 = today, 1 = tomorrow, etc.
}

// "time ago" - deterministic if you pass a fixed "now" (e.g., from server) or freeze with state.
export function getTimeAgo(timestampMs: number, nowMs: number = Date.now()): string {
	let secondsAgo = Math.floor((nowMs - timestampMs) / 1000);
	if (secondsAgo < 30) return 'Just now';
	secondsAgo = Math.round(secondsAgo / 5) * 5;
	if (secondsAgo < 60) return `A few seconds ago`;

	const minutes = Math.floor(secondsAgo / 60);
	if (minutes < 60) return `${minutes > 1 ? 'A few minutes' : 'A minute'} ago`;

	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours > 1 ? 'A few hours' : 'An hour'} ago`;

	const days = Math.floor(hours / 24);
	if (days < 7) return `${days > 1 ? 'A few days' : 'A day'} ago`;

	const weeks = Math.floor(days / 7);
	if (weeks < 4) return `${weeks > 1 ? 'A few weeks' : 'A week'} ago`;

	const months = Math.floor(days / 30);
	if (months < 12) return `${months > 1 ? 'A few months' : 'A month'} ago`;

	const years = Math.floor(days / 365);
	return `${years > 1 ? 'A few years' : 'A year'} ago`;
}

export function getTodayDateString(): string {
	const p = getBrisbaneParts(new Date());
	const dd = String(p.day).padStart(2, '0');
	const mm = String(p.month).padStart(2, '0');
	return `${dd}-${mm}-${p.year}`; // dd-mm-yyyy
}

export function getDayLastLetters(day: number): string {
	if (day >= 11 && day <= 13) return 'th';
	switch (day % 10) {
		case 1:
			return 'st';
		case 2:
			return 'nd';
		case 3:
			return 'rd';
		default:
			return 'th';
	}
}
